import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

// Registers the <admin-user-form> and <admin-user-detail> custom elements used
// by the template.
import "../../components/admin/form";
import "../../components/admin/detail";

import template from "./AdminUserTemplate.html";
import { _lcs, locale } from "../../i18n";
import { gridLanguage } from "../../core/grid";

// Both maps below hold German source strings, which are also their dictionary
// keys: module constants are evaluated before I18n exists, so the translation
// happens where they are read.
const STATUS_LABELS = {
    ok            : "Aktiv",
    not_validated : "Nicht verifiziert",
    blocked       : "Gesperrt",
    banned        : "Gebannt"
};

// Role key -> display label. Up to three roles per user (admin / client / guest).
const ROLE_LABELS = {
    admin  : "Admin",
    client : "Client",
    guest  : "Gast"
};

/**
 * AdminUser — lists all platform users whose role contains "admin" in a
 * dynamic (searchable/sortable/paginated) GridJS table. Data comes from the
 * admin-only endpoint GET /admin/user; non-admin users are filtered out client
 * side. Supports view / edit / delete per row and create via a modal form.
 */
export class AdminUser extends InAppState
{
    static ROUTE = '/admin/users';
    static ID = 'admin-users';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="users-state"]' );

        // Create / edit form + read-only details (web components).
        this.adminForm = root.querySelector( '[data-id="admin-form"]' );
        this.adminDetail = root.querySelector( '[data-id="admin-detail"]' );

        this.adminsById = new Map();

        this.bindUi( root );
        await this.loadUsers();
    }

    bindUi( root )
    {
        // Tracked listeners — InfrontJS removes them automatically on state exit.

        // Create / edit form (web component).
        this.addEventListener( root.querySelector( '[data-id="btn-new-admin"]' ), "click", () => this.adminForm.openCreate() );
        this.addEventListener( this.adminForm, "admin-user-saved", () => this.loadUsers() );

        // Row actions — delegated so it survives GridJS re-renders (sort/search/page).
        this.addEventListener( this.stateEl, "click", ( e ) => this.onGridAction( e ) );
    }

    onGridAction( e )
    {
        const btn = e.target.closest( "[data-action]" );
        if ( !btn || !this.stateEl.contains( btn ) ) return;

        const user = this.adminsById.get( btn.getAttribute( "data-user-id" ) );
        if ( !user ) return;

        switch ( btn.getAttribute( "data-action" ) )
        {
            case "view":   this.adminDetail.open( user ); break;
            case "edit":   this.adminForm.openEdit( user ); break;
            case "delete": this.onDelete( user ); break;
        }
    }

    /* ------------------------------- Delete --------------------------------- */

    async onDelete( user )
    {
        const name = this._displayName( user ) || user.email || _lcs( "dieser Benutzer" );
        const otherRoles = Array.isArray( user.role ) ? user.role.filter( ( r ) => r !== "admin" ) : [];
        const fullDelete = otherRoles.length === 0;

        const ok = await this.app.view.confirm(
            fullDelete ? _lcs( "Benutzer löschen?" ) : _lcs( "Admin-Rolle entfernen?" ),
            fullDelete
                ? _lcs( "„{name}“ hat nur die Admin-Rolle und wird vollständig gelöscht. Das kann nicht rückgängig gemacht werden.", { name } )
                : _lcs( "„{name}“ hat weitere Rollen. Es wird nur die Admin-Rolle entfernt; das Konto bleibt bestehen.", { name } ),
            fullDelete ? _lcs( "Löschen" ) : _lcs( "Rolle entfernen" ),
            _lcs( "Abbrechen" ),
            { danger: true }
        );
        if ( !ok ) return;

        try
        {
            const { result } = await this.app.api.delete( "/admin/user/" + user.id );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected delete response." );
            }
            const deleted = result.data && result.data.deleted;
            this.app.view.toast(
                deleted ? _lcs( "Benutzer wurde gelöscht." ) : _lcs( "Admin-Rolle wurde entfernt." ),
                "success"
            );
            await this.loadUsers();
        }
        catch ( err )
        {
            this.app.view.toast( this._deleteErrorForStatus( +err?.status ), "error" );
        }
    }

    /* ------------------------------- Loading -------------------------------- */

    async loadUsers()
    {
        this.showMessage( "users", _lcs( "Lade Benutzer …" ), _lcs( "Einen Moment bitte." ) );

        let admins = [];
        try
        {
            const { result } = await this.app.api.get( "/admin/user" );
            if ( !result || +result.status !== 200 || !Array.isArray( result.data ) )
            {
                throw new Error( "Unexpected /admin/user response." );
            }
            admins = result.data.filter( ( u ) => this._isAdmin( u ) );
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "triangle-alert",
                _lcs( "Benutzer konnten nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        this.adminsById = new Map();
        admins.forEach( ( u ) => this.adminsById.set( String( u.id ), u ) );

        if ( admins.length === 0 )
        {
            this.showMessage(
                "users",
                _lcs( "Keine Admin-Benutzer" ),
                _lcs( "Es wurden keine Benutzer mit der Rolle „admin“ gefunden." )
            );
            return;
        }

        this.renderGrid( admins );
    }

    renderGrid( admins )
    {
        this.stateEl.innerHTML = "";

        const rows = admins.map( ( u ) => [
            this._displayName( u ) || "—",
            u.email || "—",
            Array.isArray( u.role ) ? u.role.join( "," ) : String( u.role || "" ),
            this._statusLabel( u.status ),
            this._formatDateShort( u.createdAt ),
            String( u.id )   // actions column — id used by the formatter
        ] );

        this.grid = new Grid( {
            columns: [
                {
                    name: _lcs( "Name" ),
                    formatter: ( cell ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                _lcs( "E-Mail" ),
                {
                    name: _lcs( "Rollen" ),
                    formatter: ( cell ) => html( this._rolesBadgesHtml( cell ) )
                },
                _lcs( "Status" ),
                _lcs( "Erstellt am" ),
                {
                    name: _lcs( "Aktionen" ),
                    sort: false,
                    formatter: ( cell ) => html( this._actionButtonsHtml( cell ) )
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 10, summary: true },
            className: { container: "admin-users__grid" },
            language: gridLanguage( _lcs( "Benutzern" ), _lcs( "Keine Benutzer gefunden" ) )
        } );

        this.grid.render( this.stateEl );
    }

    _actionButtonsHtml( id )
    {
        const safe = String( id ).replace( /"/g, "&quot;" );
        const view = this._escape( _lcs( "Ansehen" ) );
        const edit = this._escape( _lcs( "Bearbeiten" ) );
        const del  = this._escape( _lcs( "Löschen" ) );
        return '<div class="admin-users__actions">' +
            '<button type="button" class="admin-users__action" data-action="view" data-user-id="' + safe + '" title="' + view + '" aria-label="' + view + '">' +
                '<i class="icon icon--eye"></i><span>' + view + '</span>' +
            '</button>' +
            '<button type="button" class="admin-users__action" data-action="edit" data-user-id="' + safe + '" title="' + edit + '" aria-label="' + edit + '">' +
                '<i class="icon icon--pencil"></i><span>' + edit + '</span>' +
            '</button>' +
            '<button type="button" class="admin-users__action admin-users__action--danger" data-action="delete" data-user-id="' + safe + '" title="' + del + '" aria-label="' + del + '">' +
                '<i class="icon icon--trash-2"></i><span>' + del + '</span>' +
            '</button>' +
        '</div>';
    }

    /**
     * Render a centered icon + heading + text block (loading / empty / error),
     * reusing the shared .content__empty styling.
     */
    showMessage( icon, title, text )
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* noop */ }
            this.grid = null;
        }
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
            '</div>';
        // textContent assignment avoids HTML injection from any dynamic strings.
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    async exit()
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* DOM is cleared below anyway */ }
            this.grid = null;
        }
        // Base InAppState clears the page-content container.
        await super.exit();
    }

    /* ------------------------------- Helpers -------------------------------- */

    _isAdmin( user )
    {
        const role = user ? user.role : null;
        if ( Array.isArray( role ) )
        {
            return role.some( ( r ) => String( r ).toLowerCase() === "admin" );
        }
        return String( role || "" ).toLowerCase() === "admin";
    }

    _displayName( user )
    {
        return ( user && user.data && user.data.name ) ? String( user.data.name ) : "";
    }

    _statusLabel( status )
    {
        return STATUS_LABELS[ status ] ? _lcs( STATUS_LABELS[ status ] ) : ( status || "—" );
    }

    /**
     * Render the role value (array or comma-separated string) as colored badges.
     */
    _rolesBadgesHtml( value )
    {
        const roles = Array.isArray( value )
            ? value
            : String( value || "" ).split( "," ).map( ( r ) => r.trim() ).filter( Boolean );

        if ( roles.length === 0 )
        {
            return '<span class="role-badge role-badge--none">—</span>';
        }

        const badges = roles.map( ( r ) =>
        {
            const key = String( r ).toLowerCase();
            const known = ROLE_LABELS.hasOwnProperty( key );
            const label = known ? this._escape( _lcs( ROLE_LABELS[ key ] ) ) : this._escape( r );
            const mod = known ? key : "other";
            return '<span class="role-badge role-badge--' + mod + '">' + label + '</span>';
        } );

        return '<span class="role-badges">' + badges.join( "" ) + '</span>';
    }

    _escape( str )
    {
        return String( str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    // Date only (no time) — used for the grid column.
    _formatDateShort( value )
    {
        if ( !value )
        {
            return "—";
        }
        const d = new Date( value );
        if ( isNaN( d.getTime() ) )
        {
            return String( value );
        }
        return new Intl.DateTimeFormat( locale(), {
            day: "2-digit", month: "2-digit", year: "numeric"
        } ).format( d );
    }

    _deleteErrorForStatus( status )
    {
        switch ( status )
        {
            case 400: return _lcs( "Aktion nicht möglich (ggf. eigenes Konto)." );
            case 401:
            case 403: return _lcs( "Keine Berechtigung für diese Aktion." );
            case 404: return _lcs( "Benutzer wurde nicht gefunden." );
            default:  return _lcs( "Löschen fehlgeschlagen. Bitte versuche es später erneut." );
        }
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Benutzer" ),
            subtitle: _lcs( "Administratoren der Plattform" )
        };
    }
}
